import React from "react";
import SEO from "../components/SEO";
import CtaBand from "../components/CtaBand";
import Testimonials from "../components/Testimonials";
import { motion } from "framer-motion";
import { Building2, Globe2, Users, Award } from "lucide-react";
import { useI18n } from "../context/I18nContext";
import ExtraPageBanner from "../components/ExtraPageBanner";

const ICONS = [Building2, Globe2, Users, Award];

export default function AboutPage() {
  const { t, tNode } = useI18n();
  const stats = tNode("about.stats") || [];
  const values = tNode("about.values") || [];

  return (
    <>
      <SEO
        title={t("about.seo_title")}
        description={t("about.seo_description")}
        keywords={t("about.seo_keywords")}
      />

      <ExtraPageBanner slug="about" testid="about-banner" />

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <span className="text-xs tracking-[0.2em] uppercase text-[#1a5193] font-chivo font-bold">
              {t("about.story_eyebrow")}
            </span>
            <h2 className="font-chivo font-black text-3xl sm:text-4xl text-[#082c50] tracking-tight mt-3 mb-6">
              {t("about.story_title_a")} <br />
              {t("about.story_title_b")}
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>{t("about.story_p1")}</p>
              <p>{t("about.story_p2")}</p>
              <p>{t("about.story_p3")}</p>
            </div>
          </div>
          <div>
            <img
              src="https://images.unsplash.com/photo-1664382953403-fc1ac77073a0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHx3YXJlaG91c2UlMjB3b3JrZXJ8ZW58MHx8fHwxNzc3MjQ3MjE2fDA&ixlib=rb-4.1.0&q=85"
              alt="Warehouse team"
              className="w-full aspect-[4/3] object-cover"
            />
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#082c50] text-white" data-testid="about-stats">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="border-l-2 border-[#1a5193] pl-4"
            >
              <div className="font-chivo font-black text-4xl sm:text-5xl">{s.v}</div>
              <div className="text-xs uppercase tracking-wider text-gray-400 mt-1">{s.l}</div>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="text-xs tracking-[0.2em] uppercase text-[#1a5193] font-chivo font-bold">
            {t("about.values_eyebrow")}
          </span>
          <h2 className="font-chivo font-black text-3xl sm:text-4xl text-[#082c50] tracking-tight mt-3 mb-12">
            {t("about.values_title")}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v, i) => {
              const Icon = ICONS[i] || Building2;
              return (
                <div key={i} className="bg-[#F8F9FA] border border-gray-200 p-6" data-testid={`value-${i}`}>
                  <Icon className="w-7 h-7 text-[#1a5193] mb-4" />
                  <h3 className="font-chivo font-bold text-lg text-[#082c50]">{v.title}</h3>
                  <p className="text-sm text-gray-600 mt-2 leading-relaxed">{v.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <Testimonials />
      <CtaBand />
    </>
  );
}
